import { ViewController } from '@estee/elc-service';
import { action, computed, observable } from 'mobx';
import {
    IAutoReplenishment,
    IAutoReplenishOptions
} from '../../exported/auto-replenishment/AutoReplenishment';
import { DAYS } from '~constants/AutoReplenishment';
import { TIMING_TYPE } from '~constants/AutoReplenishmentTimingTypes';

export interface IAutoReplenishmentControllerConfig {
    autoReplenishment: IAutoReplenishOptions;
    onSelect(option: number): void;
}

export class AutoReplenishmentViewController extends ViewController {
    private config: IAutoReplenishmentControllerConfig;
    @observable public isAutoReplenishSelected = false;
    @observable private _selectedAutoReplenishOption: number = 0;

    /**
     * Sets the config.
     *
     * @remarks
     * If a default auto replenishment option is present, set the currently
     * selected option without triggering the onSelect event.
     */
    @action
    public initialize = ({ config }: IAutoReplenishment) => {
        this.config = config as IAutoReplenishmentControllerConfig;

        const {
            autoReplenishment: { defaultInterval, timingType }
        } = this.config;

        if (defaultInterval) {
            const daysPerInterval = DAYS[timingType ?? TIMING_TYPE.DAYS];
            const defaultAutoReplenishOption = defaultInterval * daysPerInterval;

            this.selectedAutoReplenishOption = defaultAutoReplenishOption;
        }
    };

    @computed
    public get componentConfig() {
        const { autoReplenishment = {} } = this.config || {};
        const { ...autoReplenishmentConfig } = autoReplenishment;

        return { ...autoReplenishmentConfig };
    }

    @computed
    public get data() {
        const { intervals = [], toggleType, timingType } = this.componentConfig;

        return {
            componentConfig: this.componentConfig,
            isAutoReplenishSelected: this.isAutoReplenishSelected,
            selectedAutoReplenishOption: this.selectedAutoReplenishOption,
            intervals,
            toggleType,
            timingType
        };
    }

    @computed
    public get infoModalConfig() {
        const { infoPopupTriggerType, infoPopupNodeId } = this.componentConfig;

        return {
            infoPopupTriggerType,
            infoPopupNodeId
        };
    }

    @computed
    public get selectedAutoReplenishOption() {
        return this._selectedAutoReplenishOption;
    }

    /**
     * Sets the auto replenishment option
     *
     * @remarks
     * NADTX0-1758 PG expects 7 Weeks as 120 days now (instead of 119).
     *
     * {@link https://jira.esteeonline.com/browse/NADTX0-1758}
     */
    public set selectedAutoReplenishOption(value: number) {
        this._selectedAutoReplenishOption = value === 119 ? 120 : value;
    }

    @action
    public setAutoReplenishOption = (option: number) => {
        this.selectedAutoReplenishOption = option;
        this.config.onSelect(this.selectedAutoReplenishOption);
    };

    @action
    public setAutoReplenishSelection = (isSelected: boolean) => {
        this.isAutoReplenishSelected = isSelected;

        if (this.isAutoReplenishSelected) {
            this.config.onSelect(this.selectedAutoReplenishOption);
        } else {
            this.config.onSelect(0);
        }
    };

    public toggleAutoReplenishSelection = () => {
        this.setAutoReplenishSelection(!this.isAutoReplenishSelected);
    };
}
